import React, { useState, useEffect } from 'react'
import {
    Button,
    Typography,
    Box,
    Divider,
    Chip,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Avatar,
    Stack,
    Card,
    Tabs,
    Tab,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMore from '@mui/icons-material/ExpandMore'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import IconButton from '@mui/material/IconButton'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ChatIcon from '@mui/icons-material/Chat'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import CommentIcon from '@mui/icons-material/Comment'
import LockIcon from '@mui/icons-material/Lock'
import { FormattedMessage, injectIntl } from 'react-intl'
import moment from 'moment'
import Api from '../../../services/api'
import { makeStyles, useTheme } from '@mui/styles'
import DOMPurify from 'dompurify'
import Api2Talk from 'src/services/api2talk'
import { renderToStaticMarkup } from 'react-dom/server'
import parse from 'html-react-parser'
import { getFileDownloadLink } from 'src/services/attachment.service'
import { PapiroConsole } from 'src/utils/papiroConsole'


// Detecta o tema atual baseado nas variáveis CSS
const detectTheme = () => {
    const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--mexx-green-primary')
        .trim()

    const isBlueTheme = primaryColor.includes('2979ff') || primaryColor.includes('#2979ff')
    return isBlueTheme ? 'blue' : 'green'
}

// Cores do tema para campos personalizados
const getThemeColors = (themeName) => {
    if (themeName === 'blue') {
        return {
            primary: '#1E88E5',
            light: '#E3F2FD',
            dark: '#1565C0',
            border: '#BBDEFB',
            accent: '#0D47A1',
        }
    } else {
        return {
            primary: '#558B2F',
            light: '#F1F8E9',
            dark: '#33691E',
            border: '#DCEDC8',
            accent: '#1B5E20',
        }
    }
}

const useStyles = makeStyles((theme) => ({
    panelContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    panelContent: {
        padding: theme.spacing(2.5),
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'block',
        minHeight: 0,
        backgroundColor: '#FFFFFF',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
        },
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CFD8DC',
            borderRadius: '3px',
            '&:hover': {
                backgroundColor: '#B0BEC5',
            }
        }
    },
    infoSection: {
        marginBottom: theme.spacing(2),
    },
    label: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
        color: '#546E7A',
        fontSize: '0.8rem',
    },
    value: {
        marginBottom: theme.spacing(1),
        wordBreak: 'break-word',
        color: '#37474F',
        fontSize: '0.875rem',
    },
    colorIndicator: {
        width: 12,
        height: 12,
        borderRadius: '50%',
        display: 'inline-block',
        marginRight: '6px',
    },
    descriptionBox: {
        padding: theme.spacing(2),
        backgroundColor: '#FAFAFA',
        borderRadius: '8px',
        marginTop: theme.spacing(1),
        border: '1px solid #ECEFF1',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '4px',
        },
        '& a': {
            wordBreak: 'break-all',
        },
        '& pre, & code': {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        },
    },
    timestamp: {
        fontSize: '0.6875rem',
        color: '#718096',
        fontWeight: 500,
    },
    infoBar: {
        padding: theme.spacing(2, 3),
        backgroundColor: 'white',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    infoLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#718096',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: theme.spacing(0.5),
    },
    infoValue: {
        fontSize: '0.875rem',
        color: '#2D3748',
        fontWeight: 500,
    },
    attachmentCard: {
        marginTop: theme.spacing(1.25),
        padding: theme.spacing(1.25, 1.5),
        backgroundColor: 'rgba(0,0,0,0.02)',
        border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderColor: theme.palette.primary.main,
            transform: 'translateX(2px)',
        },
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: theme.spacing(4),
    },
    emptyIcon: {
        fontSize: 64,
        color: '#CBD5E0',
        marginBottom: theme.spacing(2),
    },
    attachmentItem: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        cursor: 'pointer',
        border: '1px solid #ECEFF1',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        backgroundColor: '#FAFAFA',
        '&:hover': {
            backgroundColor: '#F5F5F5',
            borderColor: theme.palette.primary.main,
            transform: 'translateX(3px)',
        },
    },
    attachmentIcon: {
        marginRight: theme.spacing(1.5),
        color: theme.palette.primary.main,
        fontSize: '1.5rem',
    },
    attachmentInfo: {
        flex: 1,
    },
    panelFooter: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        backgroundColor: '#FAFAFA',
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginTop: theme.spacing(2),
    },
    tabPanel: {
        padding: theme.spacing(2, 0),
    }
}))

function ArticleDetailDialog({ open, onClose, item, intl, setMessage }) {
    const classes = useStyles()
    const theme = useTheme()
    const [loading, setLoading] = useState(false)
    const [details, setDetails] = useState(null)
    const [descriptionExpanded, setDescriptionExpanded] = useState(false)
    const [currentTheme, setCurrentTheme] = useState('green')

    // Detecta tema atual
    useEffect(() => {
        const detected = detectTheme()
        setCurrentTheme(detected)

        const observer = new MutationObserver(() => {
            const newTheme = detectTheme()
            if (newTheme !== currentTheme) {
                setCurrentTheme(newTheme)
            }
        })

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style']
        })

        return () => observer.disconnect()
    }, [currentTheme])

    const themeColors = getThemeColors(currentTheme)

    const getFile = async attachment => {
        setLoading(true)

        let url = await getFileDownloadLink(attachment.id)
        if (url && url.data && url.data.data && url.data.success) {
            setLoading(false)
            window.open(`${url.data.data}`, '_blank')
        } else {
            setMessage(true, 'error', intl.formatMessage({ id: "file.not.found" }))
            setLoading(false)
        }
    }

    useEffect(() => {
        if (item) {
            PapiroConsole.log("Loading details for item:")
            PapiroConsole.log(item)
            setDetails(null)
            setDescriptionExpanded(false)
            loadDetails()
        }
    }, [item])

    const loadDetails = async () => {
        if (!item) return

        setLoading(true)

        try {
            let response = await Api.get(`articles/${item.id}`)

            if (response && response.data) {

                let data = response.data.success ? response.data.data : response.data

                setDetails(data || item)
            } else {
                // Handle error case
                const errorMessage = response?.response?.data?.errors
                    ? response.response.data.errors[0]
                    : intl.formatMessage({ id: "process.error" })

                if (setMessage) {
                    setMessage(true, "error", errorMessage)
                }

                Api.kickoff(response)
                setDetails(item)
            }
        } catch (error) {
            console.error('Error loading details:', error)

            if (setMessage) {
                const errorMessage = error?.response?.data?.errors
                    ? error.response.data.errors[0]
                    : intl.formatMessage({ id: "process.error" })
                setMessage(true, "error", errorMessage)
            }

            Api.kickoff(error)
            setDetails(item)
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (date) => {
        return moment(date).format('DD/MM/YYYY HH:mm')
    }

    const formatFileSize = (bytes) => {
        if (!bytes) return ''
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(1024))
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    }

    const renderArticleDetails = () => {
        if (!details) return null

        const sanitizedContent = details.content
            ? DOMPurify.sanitize(details.content)
            : ''

        const assigned = details.organization?.organizationsettings?.displayassignedinfomode == 4
            ? details.techinicalteam?.name || ''
            : details.assigneduser?.name
                ? `${details.assigneduser.name}${details.assigneduser.email ? ` (${details.assigneduser.email})` : ''}`
                : ''

        return (
            <Grid container spacing={2}>
                {/* Título (somente chamado) */}
                {
                    <Grid item xs={12}>
                        <Typography className={classes.label} variant="subtitle2">
                            <FormattedMessage id="title" defaultMessage="Título" />
                        </Typography>
                        <Typography className={classes.value} variant="body2">
                            {details.title || '-'}
                        </Typography>
                    </Grid>
                }

                {/* Descrição */}
                <Grid item xs={12}>
                    <Accordion
                        expanded={descriptionExpanded}
                        onChange={() => setDescriptionExpanded(!descriptionExpanded)}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography className={classes.label} variant="subtitle2">
                                <FormattedMessage id="content" defaultMessage="Conteúdo" />
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box
                                className={classes.descriptionBox}
                                sx={{
                                    maxHeight: '120px',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '3px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#888',
                                        borderRadius: '3px',
                                        '&:hover': {
                                            backgroundColor: '#555',
                                        }
                                    }
                                }}
                            >
                                {sanitizedContent ? (
                                    <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
                                ) : (
                                    <Typography variant="body2" color="textSecondary">-</Typography>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                {/* Autor */}
                <Grid item xs={12} md={6}>
                    <Typography className={classes.label} variant="subtitle2">
                        <FormattedMessage id="author" defaultMessage="Autor" />
                    </Typography>
                    <Typography className={classes.value} variant="body2">
                        {item.createdusername
                            ? `${item.createdusername}`
                            : '-'}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                {/* Datas e Tempo de leitura */}
                <Grid item xs={12} md={6}>
                    <Typography className={classes.label} variant="subtitle2">
                        <FormattedMessage id="created.at" defaultMessage="Criado em" />
                    </Typography>
                    <Typography className={classes.value} variant="body2">
                        {item.createdat && formatDate(item.createdat)}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography className={classes.label} variant="subtitle2">
                        <FormattedMessage id="reading.time" defaultMessage="Tempo de Leitura" />
                    </Typography>
                    <Typography className={classes.value} variant="body2" fontFamily="monospace">
                        {details.readingtime}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ mt: 2 }} />
                </Grid>
                <Grid item xs={12}>
                    <Box className={classes.tabsContainer}>
                        <Tabs value={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tab
                                icon={<AttachFileIcon />}
                                iconPosition="start"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FormattedMessage id="attachments" defaultMessage="Anexos" />
                                        {details.articleattachments && details.articleattachments.length > 0 && (
                                            <Chip
                                                label={details.articleattachments.length}
                                                size="small"
                                                sx={{ height: 18, minWidth: 18, fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </Box>
                                }
                                sx={{ textTransform: 'none', minHeight: 48 }}
                            />
                        </Tabs>
                    </Box>

                    {/* Tab Panel - Anexos */}
                    {
                        <Box className={classes.tabPanel}>
                            {details.faqattachments && details.faqattachments.length > 0 ? (
                                <Grid container spacing={1}>
                                    {details.faqattachments.map((faqAttachment, index) => {
                                        const attachment = faqAttachment.attachment
                                        if (!attachment) return null

                                        return (
                                            <Grid item xs={12} key={index}>
                                                <Paper
                                                    className={classes.attachmentItem}
                                                    elevation={0}
                                                    onClick={() => getFile(attachment)}
                                                >
                                                    <AttachFileIcon className={classes.attachmentIcon} />
                                                    <Box className={classes.attachmentInfo}>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {attachment.name || attachment.filename || attachment.description || `Anexo ${index + 1}`}
                                                        </Typography>
                                                        {attachment.size && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {formatFileSize(attachment.size)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                    <Typography variant="body2" color="textSecondary">
                                        <FormattedMessage id="no.attachments" defaultMessage="Nenhum anexo disponível" />
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    }
                </Grid>
            </Grid>
        )
    }

    const renderMainContent = () => (
        <Box className={classes.panelContent}>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                </Box>
            ) : !item ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <Typography variant="body2" color="textSecondary">
                        <FormattedMessage id="no.data" defaultMessage="Nenhum dado disponível" />
                    </Typography>
                </Box>
            ) : (
                renderArticleDetails()
            )}
        </Box>
    )

    // Quando open é passado (agent-workspace), renderiza como Dialog central
    // Quando open não é passado (sidepanel), renderiza inline
    if (open !== undefined) {
        return (
            <Dialog
                fullWidth
                maxWidth="lg"
                open={open}
                onClose={onClose}
                aria-labelledby="details-modal-title"
            >
                <DialogTitle id="details-modal-title" style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', paddingBottom: 12 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                <FormattedMessage id="article.details" defaultMessage="Detalhes do Artigo" />
                            </Typography>
                            {item && (
                                <Chip
                                    label={`#${item.id || item.articleId || ''}`}
                                    color="primary"
                                    size="small"
                                    style={{ fontWeight: 600 }}
                                />
                            )}
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                    <div style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 140px)', padding: 0 }}>
                        {renderMainContent()}
                    </div>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 1.5 }}>
                    <Button onClick={onClose} variant="contained" color="primary">
                        <FormattedMessage id="close" defaultMessage="Fechar" />
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    // Modo inline (sidepanel) — renderiza apenas o conteúdo com scroll vertical
    return (
        <div style={{ overflowY: 'auto', height: '100%' }}>
            {renderMainContent()}
        </div>
    )
}

export default injectIntl(ArticleDetailDialog)
